// src/components/exercise/exercises/MatchingExercise.tsx
import React, { useEffect, useMemo, useState } from 'react';
import type { Exercise, ExerciseUserInput, GradeResult } from '../../../types/exercise';
import { QuestionBox, FeedbackBox } from './TrueFalseExercise';
import { useThemePalette } from '../../../hooks/useThemePalette';
import { useThemeStore }   from '../../../stores/useThemeStore';

interface Props {
  exercise: Exercise;
  onChange: (input: ExerciseUserInput) => void;
  isSubmitted: boolean;
  grade: GradeResult | null;
}

function shuffledDefinitions(pairs: Array<{ word: string; definition: string }>): string[] {
  const defs = pairs.map(p => p.definition);
  for (let i = defs.length - 1; i > 0; i--) {
    const seed = pairs[i].word.charCodeAt(0) + i;
    const j = seed % (i + 1);
    [defs[i], defs[j]] = [defs[j], defs[i]];
  }
  return defs;
}

const MatchingExercise: React.FC<Props> = ({ exercise, onChange, isSubmitted, grade }) => {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';
  const pairs       = useMemo(() => exercise.pairs ?? [], [exercise]);
  const definitions = useMemo(() => shuffledDefinitions(pairs), [pairs]);
  const [selections, setSelections] = useState<Record<string, string>>({});

  useEffect(() => { setSelections({}); }, [exercise]);

  const handleSelect = (word: string, def: string) => {
    if (isSubmitted) return;
    const next = { ...selections, [word]: def };
    setSelections(next);
    onChange({ type: 'matching', selections: next });
  };

  const getPairState = (word: string, definition: string): 'default' | 'correct' | 'wrong' => {
    if (!isSubmitted) return 'default';
    const selected = selections[word];
    if (selected === definition) return 'correct';
    if (selected && selected !== definition) return 'wrong';
    return 'default';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <QuestionBox text={exercise.question} />

      {/* Definitions reference */}
      <div style={{
        background:   'rgba(79,140,247,0.05)',
        border:       '1px solid rgba(79,140,247,0.18)',
        borderRadius: '12px',
        padding:      '14px 18px',
      }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: C.accent, letterSpacing: '0.1em', marginBottom: '10px', textTransform: 'uppercase' }}>
          Options
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {definitions.map((def, i) => (
            <div key={i} style={{ fontSize: '0.86rem', color: C.muted, lineHeight: 1.5 }}>
              <span style={{ color: C.accent, fontWeight: 700, marginRight: '6px' }}>{i + 1}.</span>
              {def}
            </div>
          ))}
        </div>
      </div>

      {/* Matching rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {pairs.map((pair, idx) => {
          const state       = getPairState(pair.word, pair.definition);
          const borderColor = state === 'correct' ? C.success : state === 'wrong' ? C.error : C.border;
          const bg          = state === 'correct' ? 'rgba(52,211,153,0.06)' : state === 'wrong' ? 'rgba(248,113,113,0.06)' : C.card;

          return (
            <div key={idx} style={{
              display: 'grid', gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center', gap: '10px',
              padding: '12px 14px', borderRadius: '12px',
              border: `1px solid ${borderColor}`,
              background: bg, transition: 'all 0.2s ease',
            }}>
              <div style={{
                fontSize: '0.86rem', color: C.text, lineHeight: 1.5,
                overflow: 'hidden', display: '-webkit-box',
                WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
              }} title={pair.word}>
                <span style={{ color: C.accent, fontWeight: 700, marginRight: '6px' }}>{String.fromCharCode(65 + idx)}.</span>
                {pair.word}
              </div>

              <div style={{ color: C.muted, fontSize: '0.9rem', flexShrink: 0 }}>→</div>

              <select
                value={selections[pair.word] ?? ''}
                onChange={(e) => handleSelect(pair.word, e.target.value)}
                disabled={isSubmitted}
                style={{
                  padding:             '8px 10px',
                  borderRadius:        '8px',
                  border:              `1px solid ${borderColor}`,
                  background:          C.surface,
                  color:               selections[pair.word] ? C.text : C.muted,
                  fontSize:            '0.84rem',
                  outline:             'none',
                  cursor:              isSubmitted ? 'default' : 'pointer',
                  width:               '100%',
                  appearance:          'none',
                  backgroundImage:     `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='${isDark ? '%238b9cc4' : '%2364748b'}' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
                  backgroundRepeat:    'no-repeat',
                  backgroundPosition:  'right 10px center',
                  paddingRight:        '28px',
                  fontFamily:          'inherit',
                }}
              >
                <option value="" disabled>— select —</option>
                {definitions.map((def, i) => (
                  <option key={i} value={def}>{i + 1}. {def.length > 50 ? def.substring(0, 50) + '…' : def}</option>
                ))}
              </select>

              {isSubmitted && (
                <span style={{ fontSize: '1rem', color: state === 'correct' ? C.success : state === 'wrong' ? C.error : C.muted, marginLeft: '-8px' }}>
                  {state === 'correct' ? '✓' : state === 'wrong' ? '✗' : '–'}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {isSubmitted && grade && (
        <FeedbackBox
          isCorrect={grade.isCorrect}
          correctDisplay={!grade.isCorrect ? grade.userAnswerDisplay : undefined}
        />
      )}
    </div>
  );
};

export default MatchingExercise;
